// ENG-8092: the single teardown used by every DEPROVISION path — agent paused,
// draft, revoked, or removed from this host.
//
// Before this, each of those three sites hand-rolled the same two statements:
//
//     stopPersistentSessionAndForgetMcpBaseline(codeName);
//     tmux kill-session -t agt-<codeName>
//
// which is the Claude Code runtime and only the Claude Code runtime. An opencode
// agent runs a headless `opencode serve` in its OWN tmux namespace
// (`agt-oc-<codeName>`, see opencode-session.ts), so the kill missed it entirely
// and the serve kept running — holding its provider key in env, its MCP servers
// connected and its port bound — for an agent an operator believed they had
// paused or REVOKED. For the revoke case that is a governance problem, not a
// cosmetic one.
//
// How much still reached the agent differed by lane, and the difference is why
// this file also stops the channel ingest (see stopManagerHeldChannelIngest):
//
//   - revoked / removed-from-host DO call clearAgentCaches, which stops the
//     manager-held Slack and Telegram loops, so channel traffic ended there;
//   - PAUSED does not. It returns from processAgent before clearAgentCaches and
//     before ensureOpencodeRuntime — the only other place those loops are
//     stopped — so a paused opencode agent kept its Slack socket open and went
//     on answering. Direct chat did stop, because pollDirectChatMessages skips
//     any agent whose status is not 'active'.
//
// This is the same class of bug ENG-8000 fixed in the two RESTART lanes. It is
// deliberately NOT fixed the same way.
//
// ## Why no framework branch
//
// ENG-8000 branches on `restartTargetsOpencodeServe(framework)` because a
// restart must be followed by the matching RESPAWN — tearing down the wrong
// runtime there leaves the agent stopped, so the branch is load-bearing.
// Deprovision is terminal. Nothing respawns, so a branch buys nothing except a
// chance to get the framework wrong, and one site cannot get it right:
//
//   - paused / draft and revoked both hold a live agent record, so
//     `agent.framework` is authoritative;
//   - removed-from-host does not. The agent is gone from /host/refresh by
//     definition, leaving only `agentFrameworkCache` — which is empty after a
//     manager restart. A cold cache defaults to claude-code, so exactly the case
//     that most needs the opencode teardown is the one that would silently skip
//     it, and with the agent no longer in the poll set nothing would ever come
//     back to clean up the orphaned serve.
//
// So both teardowns run, always. This is safe because each is inert against the
// other's runtime: `tmux kill-session` against a session that does not exist
// exits non-zero (caught, no-op), `stopOpencodeSession`'s map deletes are
// no-ops for an absent entry, and no agent ever has both panes. The cost is one
// failed syscall per teardown; the benefit is that there is no framework state
// to resolve, and no fourth teardown site can inherit the wrong half.

/** The Claude Code runtime's tmux session for an agent. */
export function claudeTmuxSession(codeName: string): string {
  return `agt-${codeName}`;
}

export interface AgentRuntimeTeardownDeps {
  /**
   * Stop the opencode serve. Inert (and silent) when the agent has none: every
   * mutation inside is a map delete, and the tmux kill throws and is swallowed.
   */
  stopOpencodeServe: (codeName: string, log: (m: string) => void) => void;
  /**
   * Stop the Claude Code persistent session and drop its MCP baseline hashes.
   * Deprovision paths pass no breaker reason — an intentional teardown must not
   * count against the restart circuit breaker.
   */
  stopClaudePersistentSession: (codeName: string) => void;
  /** Kill a tmux session by name. Returns whether one was actually killed. */
  killTmuxSession: (sessionName: string) => boolean;
  /**
   * Stop the Slack Socket Mode loop and Telegram poller the MANAGER holds on an
   * opencode agent's behalf. No-op for Claude agents (their channel MCPs are
   * in-session children, reaped by killAgentChannelProcesses) and for any
   * opencode agent without a live loop.
   *
   * Load-bearing, not tidy-up: `ensureOpencodeRuntime` is the only other place
   * that stops these, and it never runs for a paused agent because processAgent
   * returns at the paused branch first. Leaving the socket open around a stopped
   * serve is strictly worse than the bug this file fixes — Socket Mode pre-acks
   * an event in ~3s, well inside a turn, so an inbound Slack message would be
   * taken from Slack and then dropped when the injection found no serve. Closing
   * the socket makes a paused agent simply absent, which is what "paused" means.
   */
  stopManagerHeldChannelIngest: (codeName: string, log: (m: string) => void) => void;
}

/**
 * Tear down whichever runtime this agent is actually running.
 *
 * `reason` is a short lane label (`paused`, `revoked`, `removed-from-host`) and
 * appears in the log line so `manager.log` attributes the teardown.
 *
 * ## Logging is conditional on purpose
 *
 * The paused branch runs on EVERY poll tick for the lifetime of a paused agent —
 * a paused agent is re-observed every ~10s and re-torn-down idempotently. An
 * unconditional log line here would put one entry per agent per tick into
 * manager.log forever. Both teardowns therefore log only when they actually
 * killed something, which in practice means one line on the tick the agent is
 * first paused and silence thereafter. That matches the pre-ENG-8092 behaviour,
 * where the `Killed tmux session for paused agent` line sat INSIDE the try block
 * after the kill and so never fired on a repeat tick.
 */
export function stopAgentRuntime(
  codeName: string,
  reason: string,
  log: (m: string) => void,
  deps: AgentRuntimeTeardownDeps,
): void {
  /**
   * Each step is independent and best-effort: they tear down different runtimes
   * and none is a precondition for another, so one throwing must not strand the
   * rest half-stopped.
   *
   * Not hypothetical (CodeRabbit, PR #3725). `stopOpencodeSlackIngest` calls
   * `entry.handle.stop()` unguarded AND before it deletes the map entry, so a
   * Socket Mode client that throws on stop leaves the entry in place, throws
   * again on the next tick, and — without this isolation — would permanently
   * block the serve teardown at every deprovision site. That is precisely the
   * "an opencode serve outlives its deprovision" bug this file exists to fix,
   * reintroduced one layer up.
   *
   * Isolating here rather than inside each dep is deliberate: the failure that
   * matters is "this teardown did not finish", and this is the only place that
   * knows the steps are siblings.
   */
  const step = (label: string, run: () => void): void => {
    try {
      run();
    } catch (err) {
      // Worth a line every time: these are all internally guarded today, so a
      // throw reaching here is genuinely exceptional rather than routine noise.
      log(`[teardown] ${label} failed for '${codeName}' (${reason}): ${(err as Error).message}`);
    }
  };

  // Close the manager-held channel loops BEFORE stopping the serve, so there is
  // no window in which the socket is live and its injection target is already
  // gone (that window is exactly how a pre-acked Slack event gets dropped).
  step('channel-ingest', () => deps.stopManagerHeldChannelIngest(codeName, log));

  // opencode next: it logs its own `[opencode-session] stopped ...` line, and
  // flips the in-memory session to 'stopped' so a later start can't short-circuit
  // on a stale 'running' (the ENG-8000 wedge).
  step('opencode-serve', () => deps.stopOpencodeServe(codeName, log));

  step('claude-session', () => deps.stopClaudePersistentSession(codeName));
  step('claude-pane', () => {
    if (deps.killTmuxSession(claudeTmuxSession(codeName))) {
      log(`[teardown] killed claude pane '${claudeTmuxSession(codeName)}' for '${codeName}' (${reason})`);
    }
  });
}
